/**
 * +field: character creation (docs/design/zones.md "One character"). The live VRM hero of the
 * picked class stands on the studio stage; the left column lists the five base classes (crest,
 * role, a two-line blurb); the right column shows what the class evolves into at level 10 (a locked
 * preview of its three specialisations, docs/design/classes/advancement.md) and takes the name (validated as you type, mirroring
 * progression's sanitizeHeroName) and CREATE. Looks: one model per class for now (no presets).
 *
 * Create → store.act('createCharacter') → the world (Mossvale town). A migrated v4 player gets
 * their old main class preselected (profile.mainClassId).
 */
import { PROFILE_CLASSES, roleOf } from '@keystone/progression';
import { BASE_SPECS } from '@keystone/content';
import type { MetaScreenFactory } from '../context';
import { el } from '../ui/dom';
import { classCrest, kmToast, primaryButton } from '../kmmo';
import { heroStageOrCrest, metaShell } from '../kmmo/extra-lobby';
import { heroFor } from '../heroes';
import { ROUTES } from '../router';
import { checkHeroName, classBlurb } from './createModel';
import './create.css';

const INFO = [
  'You play one hero. Pick one of five base classes: every class can level alone in the fields, and the Group Finder fills the other roles in dungeons.',
  'At level 10, the Tower lets your class evolve into one of three specialisations. Every ability you know evolves with it, on the same key.',
  'You start in Mossvale. Kill monsters in its fields to level up, take bounties from the board in town, and toggle Auto (Z) to let your hero hunt on its own.',
];

const ROLE_TEXT: Readonly<Record<string, string>> = { tank: 'Tank', healer: 'Healer', dps: 'Damage' };

export const createScreen: MetaScreenFactory = (ctx) => {
  const shell = metaShell(ctx, { title: 'Create your hero', back: false, exit: false, info: () => INFO, className: 'kcc' });
  const f = shell.frame;
  const classes = PROFILE_CLASSES.filter((c) => !!ctx.content.class(c));
  const pre = typeof ctx.params['preselect'] === 'string' ? (ctx.params['preselect'] as string) : ctx.store.profile.mainClassId;
  let picked = classes.includes(pre) ? pre : (classes[0] ?? 'warden');
  const stage = heroStageOrCrest(ctx, f.backdrop, { heroes: [picked], centerX: 0.5, fill: 0.7, feetY: 0.86, turn: 0.18, ring: true, reflect: true });

  // ---- class list (left)
  const left = el('section', 'kcc-classes', f.body);
  el('div', 'kcc-label', left, 'Class');
  const cards = new Map<string, HTMLElement>();
  for (const c of classes) {
    const h = heroFor(c);
    const card = el('button', 'kcc-card', left);
    card.type = 'button';
    classCrest(c, { size: 'sm' }, card);
    const t = el('div', 'kcc-card-text', card);
    const head = el('div', 'kcc-card-head', t);
    el('span', 'kcc-card-name', head, ctx.content.class(c)?.name ?? c);
    el('span', `kcc-role r-${roleOf(c)}`, head, ROLE_TEXT[roleOf(c)] ?? h.roleLabel);
    el('div', 'kcc-card-blurb', t, classBlurb(c));
    card.addEventListener('click', () => pick(c));
    cards.set(c, card);
  }

  // ---- hero summary + name (right)
  const right = el('section', 'kcc-side', f.body);
  const title = el('div', 'kcc-class-title', right);
  const role = el('div', 'kcc-class-role', right);
  // "Evolves into": the three level-10 specialisations (preview only; chosen at the Tower).
  const evolveBox = el('div', 'kcc-evolve', right);
  const evolveHead = el('div', 'kcc-evolve-head', evolveBox);
  el('span', 'kcc-label', evolveHead, 'Evolves into');
  el('span', 'kcc-evolve-lock', evolveHead, 'Chosen at the Tower, level 10');
  const evolveList = el('div', 'kcc-evolve-list', evolveBox);
  el('div', 'kcc-rule', right);
  el('label', 'kcc-label', right, 'Name').setAttribute('for', 'kcc-name');
  const input = el('input', 'kcc-input', right) as HTMLInputElement;
  input.id = 'kcc-name';
  input.maxLength = 24;
  input.autocomplete = 'off';
  input.spellcheck = false;
  input.placeholder = 'Your hero’s name';
  const err = el('div', 'kcc-error', right);
  const hint = el('div', 'kcc-hint', right, 'Letters only, 2–16 characters. Spaces, hyphens and apostrophes between letters are fine.');
  const create = primaryButton('CREATE', { size: 'lg', className: 'kcc-create', onClick: () => void submit() }, right);
  el('div', 'kcc-note', right, 'One hero per save. You begin in Mossvale.');

  let touched = false;
  const validate = (): ReturnType<typeof checkHeroName> => {
    const r = checkHeroName(input.value);
    err.textContent = touched && !r.ok ? r.error : '';
    input.classList.toggle('bad', touched && !r.ok);
    hint.classList.toggle('hidden', touched && !r.ok);
    create.disabled = !r.ok;
    return r;
  };
  input.addEventListener('input', () => {
    touched = true;
    validate();
  });
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') void submit();
  });

  function pick(c: string): void {
    picked = c;
    for (const [id, card] of cards) card.classList.toggle('selected', id === c);
    title.textContent = ctx.content.class(c)?.name ?? c;
    role.textContent = `${ROLE_TEXT[roleOf(c)] ?? ''} · ${heroFor(c).title}`;
    evolveList.replaceChildren();
    for (const sp of BASE_SPECS[c] ?? []) {
      const row = el('div', `kcc-spec${sp.launch ? '' : ' later'}`, evolveList);
      const head = el('div', 'kcc-spec-head', row);
      el('span', 'kcc-spec-name', head, sp.name);
      el('span', `kcc-role r-${sp.role}`, head, ROLE_TEXT[sp.role] ?? sp.role);
      el('span', 'kcc-spec-lock', head, 'Lv 10');
      el('div', 'kcc-spec-blurb', row, sp.blurb);
    }
    stage.setHeroes([c]);
  }

  let busy = false;
  async function submit(): Promise<void> {
    touched = true;
    const r = validate();
    if (!r.ok || busy) return;
    busy = true;
    create.disabled = true;
    const out = await ctx.store.act('createCharacter', { classId: picked, name: r.name, look: 0 });
    busy = false;
    if (!out.ok) {
      err.textContent = out.reason;
      create.disabled = false;
      kmToast(out.reason, 'error');
      return;
    }
    ctx.store.flush();
    ctx.router.reset(ROUTES.lobby);
  }

  pick(picked);
  validate();
  queueMicrotask(() => input.focus());
  return {
    el: shell.el,
    // Nothing to go back to: Esc stays here.
    onBack: () => true,
    dispose() {
      stage.dispose();
      shell.dispose();
    },
  };
};
